import { useEffect, useMemo, useState } from 'react'
import { useForm, useFieldArray, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useOnboardingStore, STEPS } from '../stores/onboardingStore'
import { useStepSubmit } from '../context/StepSubmitContext'
import AddAnotherButton from '../components/form/AddAnotherButton'
import AddressAutocomplete from '../components/form/AddressAutocomplete'
import Field from '../components/form/Field'
import FieldRow from '../components/form/FieldRow'
import Card from '../components/form/Card'
import StepLayout from '../components/form/StepLayout'
import Band from '../components/form/Band'
import ErrorBanner from '../components/form/ErrorBanner'
import { useStepErrorSummary } from '../hooks/useStepErrorSummary'
import { personalizeTitle } from '../lib/formatters'
import { flattenErrorCount } from '../lib/validators'
import { US_STATE_NAMES, stateFullName } from '../lib/states'
import { isValidationDisabled } from '../lib/validationMode'

const STEP_ID = 'locations'

function makeLocationId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `loc_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`
}

const strictSchema = z
  .object({
    locations: z
      .array(
        z.object({
          id: z.string().min(1),
          addressLine1: z.string().trim().min(3, 'Add a street address'),
          addressLine2: z.string().optional(),
          city: z.string().trim().min(2, 'Add the city'),
          state: z.enum(US_STATE_NAMES, { message: 'Select a state' }),
          zip: z.string().regex(/^\d{5}(-\d{4})?$/, 'Zip must be 5 digits (or ZIP+4)'),
          country: z.string().default('United States'),
          lat: z.number().nullable().optional(),
          lng: z.number().nullable().optional(),
          isPrimary: z.boolean(),
        }),
      )
      .min(1, 'Add at least one location'),
  })
  .superRefine((data, ctx) => {
    const primaryCount = data.locations.filter(
      (l) => l.isPrimary === true,
    ).length
    if (primaryCount !== 1) {
      ctx.addIssue({
        code: 'custom',
        path: ['locations'],
        message: 'Mark exactly one location as primary',
      })
    }
  })
const looseSchema = z.object({
  locations: z.array(z.any()).optional(),
}).passthrough()
const schema = isValidationDisabled() ? looseSchema : strictSchema

const emptyLocation = {
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zip: '',
  country: 'United States',
  lat: null,
  lng: null,
  isPrimary: false,
}

function newLocation(partial) {
  return { ...emptyLocation, ...(partial || {}), id: partial?.id || makeLocationId() }
}

export default function Step4Locations() {
  const step = STEPS.find((s) => s.id === 'locations')
  const practiceName = useOnboardingStore(
    (s) => s.formData?.details?.practiceName,
  )
  const persisted = useOnboardingStore((s) => s.formData[STEP_ID])
  const liteMode = useOnboardingStore((s) => s.liteMode)
  const providersData = useOnboardingStore(
    (s) => s.formData?.providers?.providers,
  )
  const teamsData = useOnboardingStore((s) => s.formData?.teams?.teams)
  const suggested = useOnboardingStore((s) => s.formData?.suggested)
  const updateFormData = useOnboardingStore((s) => s.updateFormData)
  const nextStep = useOnboardingStore((s) => s.nextStep)
  const { setHandler, clearHandler, reportErrorCount, pulse, focusFirstError } =
    useStepSubmit()
  const [suggestedBadgeDismissed, setSuggestedBadgeDismissed] = useState(false)

  const defaultValues = useMemo(
    () => {
      if (persisted?.locations && persisted.locations.length > 0) {
        return { locations: persisted.locations.map((l) => newLocation(l)) }
      }
      const sa = suggested?.address
      if (sa && (sa.addressLine1 || sa.city)) {
        return {
          locations: [
            newLocation({
              addressLine1: sa.addressLine1 || '',
              addressLine2: '',
              city: sa.city || '',
              // Normalize abbrev → full name so the <select> holds a valid
              // US_STATE_NAMES enum value (else strict validation blocks
              // Continue silently). Trim zip for the same reason.
              state: stateFullName(sa.state),
              zip: (sa.zip || '').trim(),
              country: sa.country || 'United States',
              lat: sa.lat ?? null,
              lng: sa.lng ?? null,
              isPrimary: true,
            }),
          ],
        }
      }
      return { locations: [newLocation({ isPrimary: true })] }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const {
    control,
    register,
    handleSubmit,
    setValue,
    trigger,
    reset,
    formState: { errors, touchedFields, dirtyFields },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })


  const { fields, append, remove } = useFieldArray({
    control,
    name: 'locations',
  })

  const watched = useWatch({ control, name: 'locations' })

  useEffect(() => {
    const locs = watched || []
    if (locs.length === 0) return
    const primaryIdxs = locs
      .map((l, i) => (l?.isPrimary === true ? i : -1))
      .filter((i) => i !== -1)
    if (primaryIdxs.length === 1) return
    if (primaryIdxs.length === 0) {
      setValue('locations.0.isPrimary', true, { shouldDirty: false })
      return
    }
    primaryIdxs.slice(1).forEach((i) => {
      setValue(`locations.${i}.isPrimary`, false, { shouldDirty: false })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watched?.length, watched?.map?.((l) => l?.isPrimary).join(',')])

  const setPrimary = (targetIdx) => {
    ;(watched || []).forEach((_, i) => {
      setValue(`locations.${i}.isPrimary`, i === targetIdx, {
        shouldDirty: true,
      })
    })
  }

  const removeWithPrimaryHandoff = (targetIdx) => {
    const locs = watched || []
    const target = locs[targetIdx]
    const targetId = target?.id

    const providerHits = targetId
      ? (providersData || []).filter((p) =>
          Array.isArray(p?.locationIds) && p.locationIds.includes(targetId),
        ).length
      : 0
    const teamHits = targetId
      ? (teamsData || []).filter((t) =>
          Array.isArray(t?.locationIds) && t.locationIds.includes(targetId),
        ).length
      : 0

    if (providerHits > 0 || teamHits > 0) {
      const pStr = `${providerHits} provider${providerHits === 1 ? '' : 's'}`
      const tStr = `${teamHits} team member${teamHits === 1 ? '' : 's'}`
      const ok = window.confirm(
        `This location is assigned to ${pStr} and ${tStr}. Removing it will unassign them. Continue?`,
      )
      if (!ok) return

      if (providersData && providerHits > 0) {
        const nextProviders = providersData.map((p) => ({
          ...p,
          locationIds: (p?.locationIds || []).filter((id) => id !== targetId),
        }))
        updateFormData('providers', { providers: nextProviders })
      }
      if (teamsData && teamHits > 0) {
        const nextTeams = teamsData.map((t) => ({
          ...t,
          locationIds: (t?.locationIds || []).filter((id) => id !== targetId),
        }))
        updateFormData('teams', { teams: nextTeams })
      }
    }

    remove(targetIdx)
  }

  const onValid = (data) => {
    // Primary-location UI was removed (2026-06 design pass); silently force
    // the first location to be the primary so the API payload is unchanged.
    const normalized = (data.locations || []).map((l, i) => ({
      ...l,
      isPrimary: i === 0,
    }))
    updateFormData(STEP_ID, { locations: normalized })
    nextStep()
  }
  const { submitAttempted, errorMessages, onInvalid } = useStepErrorSummary(
    STEP_ID,
    errors,
  )

  useEffect(() => {
    const submit = handleSubmit(onValid, onInvalid)
    setHandler(() => submit())
    return () => setHandler(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSubmit, setHandler])

  useEffect(() => {
    reportErrorCount(flattenErrorCount(errors))
  }, [errors, reportErrorCount])

  useEffect(() => {
    return () => reportErrorCount(0)
  }, [reportErrorCount])

  useEffect(() => {
    if (persisted && Object.keys(persisted).length > 0) trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // React to re-enrichment on Step 1: when suggested.address gets new content
  // and the user hasn't touched row 0, replace row 0 with the new address.
  // Only fires when there's exactly one location (i.e. the original prefilled
  // row hasn't been split into multiple) and the row's address fields are
  // both undirty AND untouched. Clearing the suggestion (null) does NOT clear
  // row 0 — only updates with new content do.
  const sa = suggested?.address
  const saKey = sa
    ? `${sa.addressLine1 || ''}|${sa.city || ''}|${sa.state || ''}|${sa.zip || ''}`
    : ''
  useEffect(() => {
    if (!sa || !sa.addressLine1) return
    const locs = watched || []
    if (locs.length !== 1) return
    const row0Dirty = dirtyFields?.locations?.[0] || {}
    const row0Touched = touchedFields?.locations?.[0] || {}
    const fields = ['addressLine1', 'addressLine2', 'city', 'state', 'zip']
    const userEdited = fields.some(
      (f) => row0Dirty[f] === true || row0Touched[f] === true,
    )
    if (userEdited) return
    // Skip if row 0 already matches — avoids redundant resets when the form
    // re-mounts with already-applied suggested values.
    if (locs[0]?.addressLine1 === sa.addressLine1 && locs[0]?.city === sa.city) return
    setValue('locations.0.addressLine1', sa.addressLine1 || '', { shouldDirty: false, shouldValidate: false })
    setValue('locations.0.addressLine2', '', { shouldDirty: false, shouldValidate: false })
    setValue('locations.0.city', sa.city || '', { shouldDirty: false, shouldValidate: false })
    setValue('locations.0.state', stateFullName(sa.state), { shouldDirty: false, shouldValidate: false })
    setValue('locations.0.zip', (sa.zip || '').trim(), { shouldDirty: false, shouldValidate: false })
    setValue('locations.0.country', sa.country || 'United States', { shouldDirty: false, shouldValidate: false })
    setValue('locations.0.lat', sa.lat ?? null, { shouldDirty: false, shouldValidate: false })
    setValue('locations.0.lng', sa.lng ?? null, { shouldDirty: false, shouldValidate: false })
    setValue('locations.0.isPrimary', true, { shouldDirty: false, shouldValidate: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saKey])

  const locationsArrayError =
    errors?.locations?.message ||
    errors?.locations?.root?.message ||
    null


  // Lite-mode editability cues — only relevant when row 0 still mirrors the
  // suggested address. As soon as any row 0 field is dirtied, hide hint+pencil.
  const row0Dirty = dirtyFields?.locations?.[0] || {}
  const row0HasAnyDirt = Object.values(row0Dirty).some(Boolean)
  const row0Suggested =
    !!suggested?.address?.addressLine1 &&
    watched?.[0]?.addressLine1 === suggested.address.addressLine1
  const showLiteHint = liteMode && row0Suggested && !row0HasAnyDirt

  return (
    <form
      className={`step-body${liteMode ? ' is-lite-locations' : ''}`}
      onSubmit={handleSubmit(onValid, onInvalid)}
      noValidate
    >
      <StepLayout
        question={personalizeTitle('Where will {practice} operate?', { practiceName })}
        helper={step.subtitle}
      >
      <Band label="Locations" description="Where your practice operates">
      <ErrorBanner show={submitAttempted} messages={errorMessages} />

      {suggested?.address &&
        !suggestedBadgeDismissed &&
        watched?.[0]?.addressLine1 === suggested.address.addressLine1 && (
          <div className="suggested-badge">
            Suggested from {suggested.domain}
            <button
              type="button"
              className="suggested-badge-dismiss"
              aria-label="Dismiss suggestion badge"
              onClick={() => setSuggestedBadgeDismissed(true)}
            >
              ×
            </button>
          </div>
        )}

      {showLiteHint && (
        <div className="lite-locations-hint" role="note">
          These are auto-filled — click any field to edit.
        </div>
      )}

      <div className="location-cards">
        {fields.map((f, idx) => {
          const loc = watched?.[idx] || {}
          const rowErr = errors?.locations?.[idx]
          const rowTouched = touchedFields?.locations?.[idx] || {}
          const rowDirty = dirtyFields?.locations?.[idx] || {}
          const gated = (field) => {
            const err = rowErr?.[field]
            if (!err || !err.message) return null
            if (!rowTouched?.[field] && !rowDirty?.[field]) return null
            return err
          }
          const addrErr = gated('addressLine1')
          const cityErr = gated('city')
          const stateErr = gated('state')
          const zipErr = gated('zip')
          const label = `Location ${idx + 1}`
          const removeAction =
            fields.length > 1 ? (
              <button
                type="button"
                className="form-card-remove"
                aria-label="Remove this location"
                onClick={() => removeWithPrimaryHandoff(idx)}
              >
                Remove
              </button>
            ) : null

          // Street-only value so this field shows just the street while the
          // dedicated City/State/ZIP fields below own those parts. (The
          // autocomplete still fills every field via onChange + setValue.)
          const autocomplete = (
            <AddressAutocomplete
              id={`locations.${idx}.addressLine1`}
              value={{ addressLine1: loc.addressLine1 }}
              hasError={!!addrErr}
              onChange={(partial) => {
                Object.entries(partial).forEach(([k, v]) => {
                  setValue(`locations.${idx}.${k}`, v, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                })
              }}
            />
          )

          return (
            <Card key={f.id} title={label} actions={removeAction}>
              <FieldRow cols={1}>
                <Field
                  label="Street address"
                  htmlFor={`locations.${idx}.addressLine1`}
                  error={addrErr}
                >
                  {liteMode && idx === 0 && row0Suggested && !row0HasAnyDirt ? (
                    <div className="lite-addr-wrap is-suggested">
                      {autocomplete}
                      <span className="lite-addr-pencil" aria-hidden="true">✏</span>
                    </div>
                  ) : (
                    autocomplete
                  )}
                </Field>
              </FieldRow>

              <FieldRow cols={1}>
                <Field label="Apt / Suite / Unit (optional)" htmlFor={`locations.${idx}.addressLine2`}>
                  <input
                    id={`locations.${idx}.addressLine2`}
                    type="text"
                    className="field-input field-input-apt"
                    placeholder="Apt / Suite / Unit (optional)"
                    autoComplete="address-line2"
                    data-lpignore="true"
                    data-form-type="other"
                    {...register(`locations.${idx}.addressLine2`)}
                  />
                </Field>
              </FieldRow>

              <FieldRow cols={3}>
                <Field label="City" htmlFor={`locations.${idx}.city`} error={cityErr}>
                  <input
                    id={`locations.${idx}.city`}
                    type="text"
                    className={`field-input${cityErr ? ' has-error' : ''}`}
                    placeholder="City"
                    autoComplete="address-level2"
                    {...register(`locations.${idx}.city`)}
                  />
                </Field>

                <Field label="State" htmlFor={`locations.${idx}.state`} error={stateErr}>
                  <select
                    id={`locations.${idx}.state`}
                    className={`field-input${stateErr ? ' has-error' : ''}`}
                    {...register(`locations.${idx}.state`)}
                  >
                    <option value="">Select</option>
                    {US_STATE_NAMES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </Field>

                <Field label="ZIP" htmlFor={`locations.${idx}.zip`} error={zipErr}>
                  <input
                    id={`locations.${idx}.zip`}
                    type="text"
                    className={`field-input${zipErr ? ' has-error' : ''}`}
                    placeholder="12345"
                    inputMode="numeric"
                    autoComplete="postal-code"
                    {...register(`locations.${idx}.zip`)}
                  />
                </Field>
              </FieldRow>

              <input type="hidden" {...register(`locations.${idx}.country`)} />
            </Card>
          )
        })}
      </div>

      {locationsArrayError && (
        <div className="field-error" style={{ marginTop: 8 }}>
          {locationsArrayError}
        </div>
      )}

      <AddAnotherButton
        label="Add another location"
        onClick={() => {
          // Focus the new row's STREET ADDRESS (Google Places) input, not the
          // Apt field. The new row's index is the current array length.
          const newIdx = fields.length
          append(newLocation())
          setTimeout(() => {
            document
              .getElementById(`locations.${newIdx}.addressLine1`)
              ?.focus()
          }, 60)
        }}
      />
      </Band>
      </StepLayout>

      <button type="submit" className="sr-only" tabIndex={-1}>
        Continue
      </button>
    </form>
  )
}
